import { useWorkspaceCapabilities } from '@/hooks/useWorkspaceCapabilities'
import { useDragReorder } from '@/hooks/useDragReorder'
import { useReorderFocus } from '@/hooks/useReorderFocus'
import { CheckCircle2, Download, List, Plus, Save, X } from 'lucide-preact'
import type { Control, FieldValues, Path, UseFieldArrayReturn } from 'react-hook-form'
import { AgendaRow } from './AgendaRow'
import { EditButton } from './EditButton'

// react-hook-form passthrough for the field-array props: this section is intentionally
// form-shape-agnostic; the concrete form type is supplied by the parent panel (OnlineEventPanel,
// etc.). A bare `any` here is deliberate and preserves caller-side typing (FieldValues is invariant
// and rejects specific forms).
//
// `control` cannot use it. Since react-hook-form 7.83 `Control` carries a `_options.validate` whose
// field-name union resolves through the form type, which makes Control invariant in *both*
// directions — `Control<any>` stopped accepting a concrete form too. Hence the type parameter.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyForm = any

interface AgendaSectionProps<T extends FieldValues> {
  editing: boolean
  saving: boolean
  editDisabled?: boolean
  /** The normalised initial agenda items from server data (trimmed, non-empty) */
  initialItems: string[]
  agendaChanged: boolean
  hasDirtyEmpty: boolean
  agendaFields: UseFieldArrayReturn<AnyForm, 'agenda', 'id'>['fields']
  control: Control<T>
  append: UseFieldArrayReturn<AnyForm, 'agenda', 'id'>['append']
  remove: UseFieldArrayReturn<AnyForm, 'agenda', 'id'>['remove']
  move: UseFieldArrayReturn<AnyForm, 'agenda', 'id'>['move']
  onEdit: () => void
  onSave: () => void
  onCancel: () => void
  /**
   * Pull agenda from BigMarker (ORB-943). Only for a web linked to a conference, and shown whether or
   * not it is signed off: BigMarker can't be sent an agenda, so pulling is the only way to realign.
   */
  pullAgenda?: { pulling: boolean; onPull: () => void; disabled?: boolean }
  /** Set once an Overwrite has saved, to say the landing page needs republishing to show it. */
  agendaUpdatedNotice?: { onGoToLandingPage?: () => void; onDismiss: () => void }
}

export function AgendaSection<T extends FieldValues>({
  editing,
  saving,
  editDisabled,
  initialItems,
  agendaChanged,
  hasDirtyEmpty,
  agendaFields,
  control,
  append,
  remove,
  move,
  onEdit,
  onSave,
  onCancel,
  pullAgenda,
  agendaUpdatedNotice,
}: AgendaSectionProps<T>) {
  const { canEdit } = useWorkspaceCapabilities()
  const { isDragOver, dragProps } = useDragReorder(move)
  const { listRef, focusAfterMove } = useReorderFocus()

  // Built from the row index at render time, so `Path<T>` cannot express it for a generic T. Named
  // here rather than cast inline, to keep the type assertion out of the markup below.
  const agendaFieldName = (idx: number) => `agenda.${idx}.value` as Path<T>

  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-5 shadow-sm">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-900 dark:text-white flex items-center gap-2">
          <List className="w-5 h-5 text-primary-600" /> Agenda
        </h2>
        {!editing ? (
          canEdit && <EditButton onClick={onEdit} disabled={editDisabled} />
        ) : (
          <div className="flex items-center gap-2">
            <button type="button" onClick={onSave} disabled={saving || hasDirtyEmpty || !agendaChanged} className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"><Save className="w-4 h-4" /> Save</button>
            <button type="button" onClick={onCancel} disabled={saving} className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"><X className="w-4 h-4" /> Cancel</button>
          </div>
        )}
      </div>
      {/* Its own line: beside Edit, the label wraps to three lines in the narrow speakers column. */}
      {pullAgenda && canEdit && !editing && (
        <button type="button" onClick={pullAgenda.onPull} disabled={pullAgenda.pulling || pullAgenda.disabled} className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50">
          {pullAgenda.pulling
            ? <span className="w-4 h-4 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
            : <Download className="w-4 h-4" />}
          {pullAgenda.pulling ? 'Pulling\u2026' : 'Pull agenda from BigMarker'}
        </button>
      )}
      {agendaUpdatedNotice && !editing && (
        <div className="mt-3 rounded-lg border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 p-3 flex items-start gap-3 text-sm text-green-800 dark:text-green-300" role="status">
          <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">
            Agenda updated. Republish the landing page to show it.
            {agendaUpdatedNotice.onGoToLandingPage && (
              <> <button type="button" onClick={agendaUpdatedNotice.onGoToLandingPage} className="underline hover:no-underline">Go to Landing Page</button></>
            )}
          </span>
          <button type="button" onClick={agendaUpdatedNotice.onDismiss} aria-label="Dismiss" className="text-green-700 dark:text-green-400 hover:opacity-70"><X className="w-4 h-4" /></button>
        </div>
      )}
      {!editing ? (
        <div className="mt-4 space-y-2">
          {initialItems.length > 0 ? initialItems.map((item, i) => (
            <div key={i} className="flex items-start gap-3"><span className="flex-shrink-0 text-xs mt-1 w-5 h-5 flex items-center justify-center rounded-full bg-primary-600 text-white font-medium">{i + 1}</span><span className="text-sm text-slate-700 dark:text-slate-300 break-words whitespace-pre-line">{item}</span></div>
          )) : <p className="text-slate-500 dark:text-slate-400 text-sm">No agenda items yet.</p>}
        </div>
      ) : (
        <div className="mt-4 space-y-4" ref={listRef}>
          {agendaFields.map((field, idx) => (
            <AgendaRow
              key={field.id}
              index={idx}
              control={control}
              name={agendaFieldName(idx)}
              isDragOver={isDragOver(idx)}
              dragProps={dragProps(idx)}
              onDelete={() => remove(idx)}
              onMoveUp={idx > 0 ? () => { move(idx, idx - 1); focusAfterMove(idx - 1) } : undefined}
              onMoveDown={idx < agendaFields.length - 1 ? () => { move(idx, idx + 1); focusAfterMove(idx + 1) } : undefined}
            />
          ))}
          <button type="button" onClick={() => append({ value: '' })} className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"><Plus className="w-4 h-4" /> Add item</button>
          <p className="text-[11px] text-slate-500 dark:text-slate-400">Drag items or focus a handle and press Arrow Up/Down to reorder.</p>
        </div>
      )}
    </div>
  )
}
